import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';
import Button from 'react-bootstrap/Button';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import { hasGlobalStations, scopeStations } from '../components/roleAccess';
import {
  buildCloseReportTemplateWorkbook, downloadCloseReportTemplate,
  buildDayVariationWorkbook, downloadDayVariation, fetchCloseReportsForDay,
} from './closeReportExcelHelper';
import CustomCloseReport from './custom_close_report';
import './CloseReports.css';

import { API_BASE } from '../config';
import { tr } from '../i18n';

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 180,
  },
  selectEmpty: { marginTop: theme.spacing(2) },
}));

// ─── Style constants ──────────────────────────────────────────────────────────
// Special value in the multi-select station picker: choosing this
// downloads every station's close report bundled into one ZIP, instead
// of the normal one-file-per-selected-station downloads. Kept distinct
// from any real station serial (which always look like "10TZ......") so
// it can never collide with an actual station.
const ALL_STATIONS_VALUE = '__ALL_STATIONS__';

function formatPeriodDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── API helpers ──────────────────────────────────────────────────────────────
const MAX_PAGES = 60;
const PER_PAGE  = 100; // the server's maximum page size
const dateOnly  = (iso) => (iso || '').slice(0, 10);

// The server narrows to this station and to reports starting on/after that
// day; the exact day is matched here. Results come newest-first by
// generated_at, so every returned page is read rather than stopping early.
// If the backend has no station_serial filter yet it returns all stations
// from that day on - slower, still correct, because of the check below.
async function findCloseReport(token, stationSerial, dateStr) {
  const params = new URLSearchParams({
    station_serial: stationSerial,
    start_date: `${dateStr}T00:00:00`,
    per_page: String(PER_PAGE),
  });
  const matches = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    params.set('page', String(page));
    const res = await fetch(
      `${API_BASE}/ak4tek/close-reports?${params.toString()}`,
      { headers: { Authorization: token } }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body    = await res.json();
    const reports = body.reports || [];

    reports.forEach((r) => {
      if (r.station_serial === stationSerial && dateOnly(r.start_date) === dateStr) matches.push(r);
    });

    if (reports.length === 0 || !body.pagination || !body.pagination.has_next) break;
  }
  if (matches.length === 0) return null;
  matches.sort((a, b) => (b.generated_at || '').localeCompare(a.generated_at || ''));
  return matches[0];
}

// ─── Today's close / update status ───────────────────────────────────────────
// One row per station: has it closed today, and has it updated today.
//   closed  = the station has a close report whose end_date is TODAY
//   updated = that same report has a submitted_at that is also TODAY
// "Today" is the browser's calendar day, so at 00:00 every station goes back
// to No / No until the managers close again in the morning - nothing is
// stored for this, it is just recalculated against the new date.
//
// Dates are compared by their first 10 characters (YYYY-MM-DD), the same way
// findCloseReport above and the Excel header already read them: the API
// sends the station's own wall-clock time (e.g. a 06:16 local close comes
// back as "...T06:16"), so the date part is already the station's date.

// How far back to page through /close-reports looking for today's closes.
// A close today has start_date = that station's PREVIOUS close, so a station
// that skipped a few days has an old start_date; 7 days covers that without
// paging through the whole history on every refresh.
const STATUS_LOOKBACK_DAYS = 7;
const STATUS_REFRESH_MS    = 5 * 60 * 1000; // auto-refresh every 5 minutes

function localDateStr(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const timeOnly = (iso) => (iso || '').slice(11, 16); // "HH:MM"

const yesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return localDateStr(d);
};

// station_serial -> today's (latest) close report for that station.
async function fetchTodayCloseStatus(token, todayStr) {
  const cutoffDate = new Date(`${todayStr}T00:00:00`);
  cutoffDate.setDate(cutoffDate.getDate() - STATUS_LOOKBACK_DAYS);
  const cutoff = localDateStr(cutoffDate);

  // Only reports started in the last STATUS_LOOKBACK_DAYS, 100 per page -
  // the server does that filtering instead of the browser paging through
  // the whole history 20 at a time.
  const params = new URLSearchParams({
    start_date: `${cutoff}T00:00:00`,
    per_page: String(PER_PAGE),
  });
  const byStation = {};
  for (let page = 1; page <= MAX_PAGES; page++) {
    params.set('page', String(page));
    const res = await fetch(
      `${API_BASE}/ak4tek/close-reports?${params.toString()}`,
      { headers: { Authorization: token } }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body    = await res.json();
    const reports = body.reports || [];

    reports.forEach((r) => {
      if (!r.station_serial || dateOnly(r.end_date) !== todayStr) return;
      const prev = byStation[r.station_serial];
      if (!prev || (r.end_date || '') > (prev.end_date || '')) byStation[r.station_serial] = r;
    });

    if (reports.length === 0 || !body.pagination || !body.pagination.has_next) break;
  }
  return byStation;
}

// ✅ / ❌ - the aria-label lets screen readers say Yes / No.
const YesNo = ({ ok }) => (
  <span role="img" aria-label={ok ? 'Yes' : 'No'} style={{ fontSize: '1.15em' }}>
    {ok ? '\u2705' : '\u274C'}
  </span>
);

// ─── React component ─────────────────────────────────────────────────────────
export default function CloseReports({ token, LogoutTimeout }) {
  const classes = useStyles();
  const [stationsArray, setStationsArray] = useState([]);
  const [stationSerials, setStationSerials] = useState([]);
  const [date,          setDate]          = useState('');
  const [busy,          setBusy]          = useState(false);
  const [message,       setMessage]       = useState('');

  // Tank vs ATG sale volume comparison export - separate filters/state
  // from the close report generator above. Stations is multi-select
  // (empty array = all stations, one sheet each); date range is now
  // start + end instead of a single date.
  const [compStationSerials, setCompStationSerials] = useState([]);
  const [compStartDate,      setCompStartDate]       = useState('');
  const [compEndDate,        setCompEndDate]          = useState('');
  const [compBusy,           setCompBusy]             = useState(false);
  const [compMessage,        setCompMessage]          = useState('');

  // Day variation (fuel shorts) of every station for one day - one Excel.
  const [varDate,    setVarDate]    = useState(yesterdayStr());
  const [varBusy,    setVarBusy]    = useState(false);
  const [varMessage, setVarMessage] = useState('');

  // Global roles (Superadmin/Admin/Auditor) see every station; Managers and
  // Cluster Managers are scoped to their associated station(s). See roleAccess.
  const isGlobal = hasGlobalStations(token);

  useEffect(() => {
    const accessTok = token?.data?.access_token || token?.access_token;
    fetch(`${API_BASE}/station/all`, { headers: { Authorization: accessTok } })
      .then((r) => r.json())
      .then((d) => {
        const vfd    = (d.data || []).filter((s) => s.is_vfd);
        const scoped = scopeStations(token, vfd);
        setStationsArray(scoped);
        // A single-station user (Manager) is locked to their one station.
        if (!isGlobal && scoped.length === 1) {
          setStationSerials([scoped[0].serial_number]);
        }
      })
      .catch(() => setMessage(tr("Failed to load stations.")));
  }, [token, isGlobal]);

  // Show a station picker when the user can choose (global = all, or a Cluster
  // Manager with more than one). A single-station Manager just sees their name.
  const showStationPicker = isGlobal || stationsArray.length > 1;

  const accessToken = token?.data?.access_token || token?.access_token;

  // ── Today's close / update status ─────────────────────────────────────
  const [statusMap,     setStatusMap]     = useState({});
  const [statusDay,     setStatusDay]     = useState(localDateStr());
  const [statusAt,      setStatusAt]      = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError,   setStatusError]   = useState('');
  const [statusOpen,    setStatusOpen]    = useState(false); // hidden until the button is clicked
  const statusRequest = useRef(0); // ignore an older load that finishes late

  const loadStatus = useCallback(async () => {
    if (!accessToken) return;
    const requestId = ++statusRequest.current;
    const today = localDateStr();
    setStatusLoading(true);
    setStatusError('');
    try {
      const map = await fetchTodayCloseStatus(accessToken, today);
      if (requestId !== statusRequest.current) return;
      setStatusMap(map);
      setStatusDay(today);
      setStatusAt(new Date());
    } catch (err) {
      if (requestId !== statusRequest.current) return;
      setStatusError(tr("Error: {0}", err.message || err));
    } finally {
      if (requestId === statusRequest.current) setStatusLoading(false);
    }
  }, [accessToken]);

  // Only while the list is open: load when it opens, then re-check every
  // minute - reload straight away when the date changes (the 00:00 reset),
  // otherwise every STATUS_REFRESH_MS. Closing it stops all of this, so a
  // hidden list never calls the API.
  useEffect(() => {
    if (!accessToken || !statusOpen) return undefined;
    loadStatus();
    let lastDay  = localDateStr();
    let lastLoad = Date.now();
    const id = setInterval(() => {
      const day = localDateStr();
      if (day !== lastDay || Date.now() - lastLoad >= STATUS_REFRESH_MS) {
        lastDay  = day;
        lastLoad = Date.now();
        loadStatus();
      }
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [accessToken, statusOpen, loadStatus]);

  const statusRows = stationsArray.map((s) => {
    const rep = statusMap[s.serial_number];
    const closed  = !!rep;
    const updated = !!(rep && rep.submitted_at && dateOnly(rep.submitted_at) === statusDay);
    return { station: s, rep, closed, updated };
  });
  const closedCount  = statusRows.filter((r) => r.closed).length;
  const updatedCount = statusRows.filter((r) => r.updated).length;

  const handleGenerate = async () => {
    setMessage('');
    if (stationSerials.length === 0) { setMessage(tr("Please select at least one station.")); return; }
    if (!date)                      { setMessage(tr("Please select a date."));    return; }

    setBusy(true);
    try {
      if (LogoutTimeout) LogoutTimeout();

      if (stationSerials.includes(ALL_STATIONS_VALUE)) {
        // All Stations: build every station's workbook the same way as
        // below, but instead of triggering N separate browser downloads,
        // pack each one's .xlsx bytes into a single ZIP and download
        // that one file.
        const zip = new JSZip();
        const succeeded = [];
        const missing    = [];
        const variationRows = [];

        for (const s of stationsArray) {
          const report = await findCloseReport(accessToken, s.serial_number, date);
          variationRows.push({ stationName: s.name, report });
          if (!report) {
            missing.push(s.name);
            continue;
          }
          const wb       = await buildCloseReportTemplateWorkbook(report, s.name, accessToken);
          const safeName = s.name.replace(/[^A-Za-z0-9_-]+/g, '_');
          const xlsxData = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          zip.file(`close_report_detail_${safeName}_${date}.xlsx`, xlsxData);
          succeeded.push(s.name);
        }

        if (succeeded.length === 0) {
          setMessage(tr("No close reports found for any station on {0}.", date));
          return;
        }

        // Every station's day variation on one sheet, next to the reports.
        zip.file(
          `day_variation_all_stations_${date}.xlsx`,
          XLSX.write(buildDayVariationWorkbook(variationRows, date), { bookType: 'xlsx', type: 'array' })
        );

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipName = `close_reports_${date}.zip`;

        const url = window.URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = zipName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        const parts = [tr('Downloaded {0} with {1} station(s).', zipName, succeeded.length)];
        if (missing.length) parts.push(tr("No close report for {0}: {1}.", date, missing.join(', ')));
        setMessage(parts.join(' '));
        return;
      }

      const succeeded = [];
      const missing    = [];

      // One full download per selected station.
      for (const serial of stationSerials) {
        const stationInfo = stationsArray.find((s) => s.serial_number === serial);
        const name = stationInfo ? stationInfo.name : serial;

        const report = await findCloseReport(accessToken, serial, date);
        if (!report) {
          missing.push(name);
          continue;
        }

        await downloadCloseReportTemplate(report, name, date, accessToken);
        succeeded.push(name);
      }

      const parts = [];
      if (succeeded.length) parts.push(tr("Downloaded: {0}.", succeeded.join(', ')));
      if (missing.length)   parts.push(tr("No close report for {0}: {1}.", date, missing.join(', ')));
      setMessage(parts.join(' ') || tr("No stations processed."));
    } catch (err) {
      setMessage(tr("Error: {0}", err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const handleDayVariation = async () => {
    setVarMessage('');
    if (!varDate) { setVarMessage(tr("Please select a date.")); return; }
    if (stationsArray.length === 0) { setVarMessage(tr("No stations.")); return; }
    setVarBusy(true);
    try {
      if (LogoutTimeout) LogoutTimeout();
      // One paged request for every station's report of that day, instead
      // of one search per station.
      const bySerial = await fetchCloseReportsForDay(accessToken, varDate);
      const rows = stationsArray.map((s) => ({ stationName: s.name, report: bySerial[s.serial_number] || null }));
      const found = rows.filter((row) => row.report).length;
      if (found === 0) {
        setVarMessage(tr("No close reports found for any station on {0}.", varDate));
        return;
      }
      const filename = downloadDayVariation(rows, varDate);
      const missingNames = rows.filter((row) => !row.report).map((row) => row.stationName);
      setVarMessage([
        tr("Downloaded {0} ({1} of {2} stations).", filename, found, rows.length),
        missingNames.length ? tr("No close report for {0}: {1}.", varDate, missingNames.join(', ')) : '',
      ].filter(Boolean).join(' '));
    } catch (err) {
      setVarMessage(tr("Error: {0}", err.message || err));
    } finally {
      setVarBusy(false);
    }
  };

  const handleTankPumpExport = async () => {
    setCompMessage('');
    setCompBusy(true);
    try {
      if (LogoutTimeout) LogoutTimeout();

      const params = new URLSearchParams();
      compStationSerials.forEach((serial) => params.append('station_serial', serial));
      if (compStartDate) params.set('start_date', compStartDate);
      if (compEndDate)   params.set('end_date', compEndDate);

      const res = await fetch(
        `${API_BASE}/ak4tek/close-reports/tank_pump_comparison?${params.toString()}`,
        { headers: { Authorization: accessToken } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const rows = body.data || [];

      if (rows.length === 0) {
        setCompMessage(tr("No records found for the selected filters."));
        return;
      }

      // Group by station_serial - one sheet per station, same as the
      // Excel layout shown (AFRICANA STATION, BAMAGA STATION, etc. as
      // separate tabs). If a single station is filtered, this naturally
      // produces just one sheet.
      const grouped = {};
      rows.forEach((row) => {
        const key = row.station_serial || 'Unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      });

      const wb = XLSX.utils.book_new();
      const usedSheetNames = new Set();
      const header = ['start_date', 'end_date', 'product', 'sale_volume', 'atg_sale_volume', 'difference'];

      Object.keys(grouped).forEach((serial) => {
        const stationInfo = stationsArray.find((s) => s.serial_number === serial);
        const label = stationInfo ? stationInfo.name : serial;

        const aoa = [header];
        const sortedRows = [...grouped[serial]].sort(
          (a, b) => new Date(b.start_date) - new Date(a.start_date)
        );
        sortedRows.forEach((r) => {
          aoa.push([
            formatPeriodDate(r.start_date),
            formatPeriodDate(r.end_date),
            r.tank_prod_name || '',
            r.sale_volume,
            r.atg_sale_volume,
            r.volume_difference,
          ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // Header row: bold white text on dark fill, centered.
        for (let c = 0; c < header.length; c++) {
          const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
          if (cell) {
            cell.s = {
              font: { bold: true, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '343A40' } },
              alignment: { horizontal: 'center' },
            };
          }
        }
        // Data rows: bold + centered too, per request.
        for (let r = 1; r < aoa.length; r++) {
          for (let c = 0; c < header.length; c++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell) {
              cell.s = {
                font:      { bold: true },
                alignment: { horizontal: 'center' },
              };
            }
          }
        }
        ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];

        // Excel sheet names: max 31 chars, no : \ / ? * [ ], must be unique.
        let sheetName = String(label).replace(/[:\\/?*[\]]/g, '').trim().slice(0, 31) || 'Station';
        let n = 2;
        while (usedSheetNames.has(sheetName)) {
          const suffix = ` (${n})`;
          sheetName = sheetName.slice(0, 31 - suffix.length) + suffix;
          n++;
        }
        usedSheetNames.add(sheetName);

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const scope = compStationSerials.length === 1
        ? (stationsArray.find((s) => s.serial_number === compStationSerials[0])?.name || compStationSerials[0])
        : compStationSerials.length > 1
          ? `${compStationSerials.length}_Stations`
          : 'All_Stations';
      const safeScope = String(scope).replace(/[^A-Za-z0-9_-]+/g, '_');
      const rangePart  = (compStartDate || compEndDate)
        ? `${compStartDate || 'start'}_to_${compEndDate || 'end'}`
        : 'all_dates';
      const filename   = `tank_pump_comparison_${safeScope}_${rangePart}.xlsx`;

      XLSX.writeFile(wb, filename);
      setCompMessage(tr("Exported {0} record(s) across {1} station(s).", rows.length, Object.keys(grouped).length));
    } catch (err) {
      setCompMessage(tr("Error: {0}", err.message || err));
    } finally {
      setCompBusy(false);
    }
  };

  return (
    <div className="CloseReportsPage">
      <div className="FilterBar">
        <p>
          <b>{tr("Today's Close Status")}</b>
          {statusOpen && statusAt && (
            <span style={{ marginLeft: 10 }}>
              {tr("as of {0}", `${statusDay} ${statusAt.toTimeString().slice(0, 5)}`)}
            </span>
          )}
        </p>

        <Button
          variant="primary"
          size="sm"
          onClick={() => setStatusOpen((open) => !open)}
          style={{ marginBottom: 8 }}
        >
          {statusOpen ? tr("Hide Close Status") : tr("Show Close Status")}
        </Button>

        {statusOpen && (<>
        <div style={{ marginBottom: 8 }}>
          <Button variant="secondary" size="sm" disabled={statusLoading} onClick={loadStatus}>
            {statusLoading ? tr("Loading…") : tr("Refresh")}
          </Button>
                    {stationsArray.length > 0 && (
            <span style={{ marginLeft: 12 }}>
              <b>{tr("Closed")}:</b> {closedCount}/{stationsArray.length}
              <span style={{ marginLeft: 12 }}>
                <b>{tr("Updated")}:</b> {updatedCount}/{stationsArray.length}
              </span>
            </span>
          )}

          <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
            {tr("Status resets daily at 00:00 (midnight).")}
          </div>
        </div>

        {statusError && <p style={{ color: '#c82333' }}><b>{statusError}</b></p>}

        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm table-bordered" style={{ maxWidth: 760, textAlign: 'center' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{tr("Station")}</th>
                <th>{tr("Closed")}</th>
                <th>{tr("Close Time")}</th>
                <th>{tr("Updated")}</th>
                <th>{tr("Update Time")}</th>
              </tr>
            </thead>
            <tbody>
              {statusRows.map(({ station, rep, closed, updated }) => (
                <tr key={station.serial_number}>
                  <td style={{ textAlign: 'left' }}>{station.name}</td>
                  <td><YesNo ok={closed} /></td>
                  <td>{closed ? timeOnly(rep.end_date) : '—'}</td>
                  <td><YesNo ok={updated} /></td>
                  <td>{updated ? timeOnly(rep.submitted_at) : '—'}</td>
                </tr>
              ))}
              {statusRows.length === 0 && (
                <tr><td colSpan={5}>{tr("No stations.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      <hr />

      <div className="FilterBar">
        <p><b>{tr("Close Report")}</b></p>

        {showStationPicker ? (
          <FormControl className={classes.formControl} style={{ minWidth: 320 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={[{ serial_number: ALL_STATIONS_VALUE, name: tr('All Stations (download as ZIP)') }, ...stationsArray]}
              getOptionLabel={(option) => option.name || ''}
              value={
                stationSerials.includes(ALL_STATIONS_VALUE)
                  ? [{ serial_number: ALL_STATIONS_VALUE, name: tr('All Stations (download as ZIP)') }]
                  : stationsArray.filter((s) => stationSerials.includes(s.serial_number))
              }
              onChange={(event, newValue) => {
                const newSerials = newValue.map((v) => v.serial_number);
                const hadAll = stationSerials.includes(ALL_STATIONS_VALUE);
                const hasAll = newSerials.includes(ALL_STATIONS_VALUE);

                if (hasAll && !hadAll) {
                  // "All Stations" was just picked - it replaces any
                  // individually-selected stations, not add to them.
                  setStationSerials([ALL_STATIONS_VALUE]);
                } else if (hasAll && hadAll && newSerials.length > 1) {
                  // A specific station was picked while "All Stations" was
                  // already selected - drop "All Stations" and keep only
                  // the specific picks.
                  setStationSerials(newSerials.filter((v) => v !== ALL_STATIONS_VALUE));
                } else {
                  setStationSerials(newSerials);
                }
              }}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox checked={selected} />
                  <ListItemText primary={option.name} />
                </>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder={tr("Search stations...")} />
              )}
            />
            <FormHelperText> {tr("Select one or more, or \"All Stations\" for a ZIP")}</FormHelperText>
          </FormControl>
        ) : (stationSerials.length > 0 && (
          <FormControl className={classes.formControl}>
            <TextField
              label={tr("Station")}
              value={stationsArray.find((s) => s.serial_number === stationSerials[0])?.name || stationSerials[0]}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        ))}

        <FormControl className={classes.formControl} noValidate>
          <TextField
            label={tr("Report Date")}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormHelperText>{tr("Day of the close report")}</FormHelperText>
        </FormControl>

        <div style={{ marginTop: 10 }}>
          <Button variant="primary" disabled={busy} onClick={handleGenerate}>
            {busy ? tr("Generating…") : tr("Download Close Report (Excel)")}
          </Button>
          {busy && (
            <span style={{ marginLeft: 10, fontStyle: 'italic' }}>
              {tr("Please wait…")}
            </span>
          )}
        </div>

        {message && <p style={{ marginTop: 10 }}><b>{message}</b></p>}
      </div>

      <hr />

      <div className="FilterBar">
        <p><b>{tr("Day Variation (fuel shorts) - all stations")}</b></p>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            label={tr("Report Date")}
            type="date"
            value={varDate}
            onChange={(e) => setVarDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormHelperText>{tr("Day of the close reports (yesterday by default)")}</FormHelperText>
        </FormControl>

        <div style={{ marginTop: 10 }}>
          <Button variant="primary" disabled={varBusy} onClick={handleDayVariation}>
            {varBusy ? tr("Generating…") : tr("Download Day Variation (Excel)")}
          </Button>
          {varBusy && (
            <span style={{ marginLeft: 10, fontStyle: 'italic' }}>
              {tr("Please wait…")}
            </span>
          )}
        </div>

        {varMessage && <p style={{ marginTop: 10 }}><b>{varMessage}</b></p>}
      </div>

      <hr />

      <CustomCloseReport stations={stationsArray} accessToken={accessToken} LogoutTimeout={LogoutTimeout} />

      <hr />

      <div className="FilterBar">
        <p><b>{tr("Pump vs ATG Sales Volume Comparison")}</b></p>

        <FormControl className={classes.formControl} style={{ minWidth: 220 }}>
          <Select
            multiple
            value={compStationSerials}
            displayEmpty
            onChange={(e) => setCompStationSerials(e.target.value)}
            renderValue={(selected) =>
              selected.length === 0
                ? tr('All Stations')
                : selected
                    .map((serial) => stationsArray.find((s) => s.serial_number === serial)?.name || serial)
                    .join(', ')
            }
            className={classes.selectEmpty}
          >
            {stationsArray.map((s, i) => (
              <MenuItem key={i} value={s.serial_number}>
                <Checkbox checked={compStationSerials.indexOf(s.serial_number) > -1} />
                <ListItemText primary={s.name} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{tr("Stations (blank = all, one sheet each)")}</FormHelperText>
        </FormControl>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            label={tr("Start Date")}
            type="date"
            value={compStartDate}
            max={compEndDate || undefined}
            onChange={(e) => setCompStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormHelperText>{tr("Filter by start date (optional)")}</FormHelperText>
        </FormControl>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            label={tr("End Date")}
            type="date"
            value={compEndDate}
            min={compStartDate || undefined}
            onChange={(e) => setCompEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormHelperText>{tr("Filter by end date (optional)")}</FormHelperText>
        </FormControl>

        <div style={{ marginTop: 10 }}>
          <Button variant="primary" disabled={compBusy} onClick={handleTankPumpExport}>
            {compBusy ? tr("Generating…") : tr("Download Pump vs ATG Sales Comparison (Excel)")}
          </Button>
          {compBusy && (
            <span style={{ marginLeft: 10, fontStyle: 'italic' }}>
              {tr("Please wait…")}
            </span>
          )}
        </div>

        {compMessage && <p style={{ marginTop: 10 }}><b>{compMessage}</b></p>}
      </div>
    </div>
  );
}